import { css, mediaQueries } from '@eti/styles';
import Media from 'react-media';
import { TripType } from '@eti/schema-types';
import type { SortType } from '@eti/schema-types';
import { price as priceUtil } from '@eti/utils';
import { useTranslation } from '@eti/providers';
import { Inline, vars } from '@etg/wings';
import { useState } from 'react';
import MobileSort from './MobileSort';
import QuickSortButton from './QuickSortButton';
import {
  getLongestQuickSortOption,
  getQuickSortOptions,
  getRecommendedQuickSortOption,
} from './utils';
import { quickSortPricesType } from './quickSortConstants';
import QuickSortDropdown from './QuickSortDropdown';

const firstQuicksortButtonStyles = css`
  border-radius: ${vars.card.borderRadius} 0 0 ${vars.card.borderRadius};

  [dir='rtl'] & {
    border-radius: 0 ${vars.card.borderRadius} ${vars.card.borderRadius} 0;
    border-right: 0;
  }
`;

const quickSortButtonsWrapperStyles = css`
  justify-content: space-between;
`;

const borderRadiusStyles = css`
  border-radius: 0 ${vars.card.borderRadius} ${vars.card.borderRadius} 0;
  border-right: 0;

  [dir='rtl'] & {
    border-radius: ${vars.card.borderRadius} 0 0 ${vars.card.borderRadius};
    border-right: 1px solid ${vars.colors.divider};
  }
`;

interface QuickSortButtonBarProps {
  isOpen: boolean;
  journeyType: TripType;
  onDismiss?: (value: boolean) => void;
  onSort: (value: string) => void;
  selectedSortType: SortType;
  sortPrices: {
    bestValue: number;
    carrierPromo: number;
    cheapTrip: number;
    recommendation: number;
    shortTrip: number;
  };
}

const QuickSortButtonBar = ({
  isOpen,
  journeyType,
  onDismiss,
  onSort,
  selectedSortType,
  sortPrices,
}: QuickSortButtonBarProps) => {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean | null>(null);
  const quickSortOptions = getQuickSortOptions(t);
  const recommendedQuickSortOption = getRecommendedQuickSortOption(t);

  const mobileSortingOptions = quickSortOptions.concat(
    recommendedQuickSortOption,
    getLongestQuickSortOption(t),
  );

  const onButtonClick = (value: keyof typeof quickSortPricesType) => {
    setIsDropdownOpen(false);
    onSort(value);
  };

  return (
    <Inline
      className={quickSortButtonsWrapperStyles}
      data-testid="Quicksort-button-bar-container-v2"
      noWrap
      spacing={0}
    >
      <Media query={mediaQueries.medium.down}>
        {(isMobile) =>
          isMobile ? (
            <MobileSort
              isOpen={isOpen}
              onDismiss={onDismiss}
              onDone={onSort}
              options={mobileSortingOptions}
              sortPrices={sortPrices}
            />
          ) : (
            <>
              {quickSortOptions.map(({ value, labelKey, desc }, index: number) => (
                <QuickSortButton
                  className={index === 0 ? firstQuicksortButtonStyles : undefined}
                  desc={desc}
                  formattedPrice={priceUtil.formatPrice(
                    sortPrices[quickSortPricesType[value] as keyof typeof sortPrices],
                  )}
                  isSelected={selectedSortType === value}
                  key={labelKey + index}
                  label={labelKey}
                  onClick={() =>
                    journeyType === TripType.Return ? onButtonClick(value) : onSort(value)
                  }
                  type={value}
                />
              ))}
              {journeyType !== TripType.Return ? (
                <QuickSortButton
                  className={borderRadiusStyles}
                  desc={recommendedQuickSortOption.desc}
                  formattedPrice={priceUtil.formatPrice(
                    sortPrices[
                      quickSortPricesType[
                        recommendedQuickSortOption.value
                      ] as keyof typeof sortPrices
                    ],
                  )}
                  isSelected={selectedSortType === recommendedQuickSortOption.value}
                  label={recommendedQuickSortOption.labelKey}
                  onClick={() => onSort(recommendedQuickSortOption.value)}
                  type={recommendedQuickSortOption.value}
                />
              ) : (
                <QuickSortDropdown
                  isDropdownOpen={isDropdownOpen}
                  onButtonClick={onSort}
                  selectedSortType={selectedSortType}
                  setIsDropdownOpen={setIsDropdownOpen}
                  sortPrices={sortPrices}
                />
              )}
            </>
          )
        }
      </Media>
    </Inline>
  );
};

export default QuickSortButtonBar;
